import { useState, type ReactNode } from "react";
import { ChatBookingCard } from "./chat-booking-card";
import { useTranslation } from "react-i18next";
import { CalendarDays, Check, Clock, MessageCircle, Moon, Users } from "lucide-react";
import { paysByLink, WHATSAPP_URL } from "@majourneys/shared";
import { CONTROL_EDGE, CONTROL_LIT } from "@majourneys/shared/ui";
import type { BookingProposal } from "../api/ai.api";
import type { ChatLang } from "../hooks/use-ai-chat";
import { markProposalOpened, type RequestSentInfo } from "../lib/proposal-status";
import { placeHref } from "./layout-parts";
import { SOFT } from "./soft-tokens";
import { panelStyle } from "../../../shared/ui/card-frame";

/**
 * The web twin of the phone's BookingProposalCard (owner, 2026-10-05: the
 * web chat said "proposed below" and showed nothing below).
 *
 * Nothing is reserved by the proposal itself. "Check details and send" opens
 * the booking questions here in the chat (owner, 2026-10-12: ChatBookingCard,
 * kit C31 to C40), which send the request through the same checkout as the
 * place's sheet. The link still points at the place's pre-filled form for a
 * new-tab click, and places that book on their own form (nightclubs,
 * transfers, paused venues) are sent there. It says "awaiting your
 * confirmation", never "booked".
 *
 * Chat kit C10 (2026-10-09): a gold "Awaiting your confirmation" tag, the
 * date, time and party as three control chips, "Check details and send" as
 * the one primary (still exactly `proposalHref`), "Change" as the secondary.
 * Once the guest has sent the request from that form, the same slot shows
 * C11, "Request sent", and only on proof: see lib/proposal-status.ts.
 */

/** "Quad Biking in Marrakech" + "Marrakech" stays one city, not two. */
export function proposalHeading(title: string, city: string | null): string {
  if (!city) return title;
  return title.toLowerCase().includes(city.toLowerCase()) ? title : `${title}, ${city}`;
}

/** The place's page with its booking form open and pre-filled. */
export function proposalHref(p: BookingProposal, lang: string): string {
  const base = p.citySlug
    ? placeHref(
        { slug: p.slug, citySlug: p.citySlug, categorySlug: p.categorySlug ?? null, segment: p.segment ?? null },
        lang,
      )
    : `/${lang}/${p.slug}`;
  const q = new URLSearchParams({ book: "1", date: p.date, time: p.time, adults: String(p.adults) });
  if (p.children > 0) q.set("children", String(p.children));
  if (p.checkOut) q.set("checkOut", p.checkOut);
  if (p.packageId) q.set("pkg", p.packageId);
  if (p.women != null && p.men != null) {
    q.set("women", String(p.women));
    q.set("men", String(p.men));
  }
  return `${base}?${q.toString()}`;
}

const CONTROL = { background: CONTROL_LIT, boxShadow: `inset 0 0 0 1px ${CONTROL_EDGE}` } as const;
const GOLD_TAG = { background: "rgba(229,178,101,0.2)", color: SOFT.goldInk } as const;
const PRIMARY = {
  background: SOFT.buttonFace,
  color: "#fff",
  boxShadow: `0 10px 24px -12px rgba(17,14,57,0.8), ${SOFT.buttonInset}`,
} as const;

/** Latin digits in every locale, the calendar day as written (no zone shift). */
function dayLabel(iso: string, lang: string): string {
  return new Intl.DateTimeFormat(`${lang}-u-nu-latn`, {
    weekday: "short",
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  }).format(new Date(`${iso.slice(0, 10)}T12:00:00Z`));
}

function Chip({ icon, children, testId }: { icon: ReactNode; children: ReactNode; testId?: string }) {
  return (
    <span
      className="flex min-h-[36px] min-w-0 items-center justify-center gap-1.5 rounded-[10px] px-2 text-center text-[12.5px] font-semibold"
      style={{ ...CONTROL, color: SOFT.ink }}
      data-testid={testId}
    >
      <span aria-hidden="true" className="shrink-0">
        {icon}
      </span>
      {/* Dates, times and party size wrap inside the chip: they are what the guest confirms. */}
      <span className="min-w-0 break-words">{children}</span>
    </span>
  );
}

/** A spa says who is coming; everyone else counts adults and children. */
function usePartyLabel(proposal: BookingProposal): string {
  const { t } = useTranslation();
  return proposal.women != null && proposal.men != null
    ? [
        ...(proposal.women > 0 ? [t("ai_concierge.proposal.women", { count: proposal.women })] : []),
        ...(proposal.men > 0 ? [t("ai_concierge.proposal.men", { count: proposal.men })] : []),
      ].join(", ")
    : [
        t("ai_concierge.proposal.adults", { count: proposal.adults }),
        ...(proposal.children > 0 ? [t("ai_concierge.proposal.children", { count: proposal.children })] : []),
      ].join(", ");
}

export function BookingProposalCard({
  proposal,
  lang,
  messageId,
  onChange,
  sent,
}: {
  proposal: BookingProposal;
  lang: ChatLang;
  /** The turn this card belongs to; lets "Request sent" find its proof later. */
  messageId?: string;
  /**
   * "Change": hands the guest back to the chat to say what to change. Absent,
   * the button is left off rather than pointing nowhere.
   */
  onChange?: () => void;
  /** Proof the request went out (C11). Only ever from lib/proposal-status. */
  sent?: RequestSentInfo | null;
}) {
  return <ProposalOrSent proposal={proposal} lang={lang} messageId={messageId} onChange={onChange} sent={sent} />;
}

/** "Request sent" from the stored proof, or at once from the chat's own send. */
function ProposalOrSent({
  proposal,
  lang,
  messageId,
  onChange,
  sent,
}: {
  proposal: BookingProposal;
  lang: ChatLang;
  messageId?: string;
  onChange?: () => void;
  sent?: RequestSentInfo | null;
}) {
  const [justSent, setJustSent] = useState<RequestSentInfo | null>(null);
  const shown = sent ?? justSent;
  if (shown) return <RequestSentCard proposal={proposal} sent={shown} lang={lang} />;
  return <ProposalCard proposal={proposal} lang={lang} messageId={messageId} onChange={onChange} onSent={setJustSent} />;
}

function ProposalCard({
  proposal,
  lang,
  messageId,
  onChange,
  onSent,
}: {
  proposal: BookingProposal;
  lang: ChatLang;
  messageId?: string;
  onChange?: () => void;
  onSent?: (info: RequestSentInfo) => void;
}) {
  const { t } = useTranslation();
  const day = dayLabel(proposal.date, lang);
  const isStay = Boolean(proposal.checkOut && proposal.nights);
  const party = usePartyLabel(proposal);
  const href = proposalHref(proposal, lang);
  // The booking questions open here, in the chat (owner, 2026-10-12). Only a
  // place that books on its own form still leaves for it (ChatBookingCard).
  const [asking, setAsking] = useState(false);
  const openForm = () => {
    if (messageId) markProposalOpened(messageId, proposal);
    window.location.assign(href);
  };

  return (
    <section
      style={{ ...panelStyle(20), padding: 12 }}
      aria-label={proposal.title}
      data-testid="booking-proposal-card"
    >
      <div className="flex min-w-0 flex-col gap-1">
        <h3 className="text-start text-[15px] font-bold leading-5" style={{ color: SOFT.ink }}>
          {proposalHeading(proposal.title, proposal.city)}
        </h3>
        {proposal.packageName ? (
          <p className="text-start text-[12.5px] font-semibold" style={{ color: SOFT.ink }} data-testid="proposal-option">
            {proposal.packageName}
          </p>
        ) : null}
        <span
          className="inline-flex w-fit items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold"
          style={GOLD_TAG}
          data-testid="proposal-tag"
        >
          <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full" style={{ background: SOFT.gold }} />
          {t("ai_concierge.proposal.pending")}
        </span>
      </div>

      <div className="mt-3 grid grid-cols-3 gap-1.5">
        {isStay ? (
          <>
            <Chip icon={<CalendarDays size={13} />} testId="proposal-when">
              {t("ai_concierge.proposal.stay_when", { from: day, to: dayLabel(proposal.checkOut!, lang) })}
            </Chip>
            <Chip icon={<Moon size={13} />}>{t("ai_concierge.proposal.nights", { count: proposal.nights })}</Chip>
          </>
        ) : (
          <>
            <Chip icon={<CalendarDays size={13} />} testId="proposal-when">
              {day}
            </Chip>
            <Chip icon={<Clock size={13} />}>{proposal.time}</Chip>
          </>
        )}
        <Chip icon={<Users size={13} />} testId="proposal-party">
          {party}
        </Chip>
      </div>

      {/* No amount. The figure on file is the cheapest price per person or
          per group, not this party's total, and a table is free; the form
          the primary opens shows the real total. Only how it is paid. */}
      <p className="mt-2.5 text-start text-[12px]" style={{ color: SOFT.body }} data-testid="proposal-payment">
        {paysByLink(proposal.slug)
          ? t("activity.pay_by_link_option")
          : proposal.categorySlug === "restaurants"
            ? // A table is held, not sold: never "pay on arrival" (owner rule).
              t("ai_concierge.booking.free_to_reserve")
            : t("booking.pay_on_arrival")}
      </p>

      {asking ? (
        <ChatBookingCard proposal={proposal} lang={lang} messageId={messageId} onHandOff={openForm} onSent={onSent} />
      ) : (
      <div className="mt-3 flex gap-2">
        <a
          href={href}
          onClick={(e) => {
            // A plain click books in the chat; a modified one (new tab) still
            // opens the place's form, which is what the link points at.
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) {
              if (messageId) markProposalOpened(messageId, proposal);
              return;
            }
            e.preventDefault();
            setAsking(true);
          }}
          className="mj-press inline-flex min-h-[40px] flex-1 items-center justify-center rounded-full px-4 text-center text-[13px] font-semibold"
          style={PRIMARY}
          data-testid="proposal-send"
        >
          {t("ai_concierge.proposal.check_send")}
        </a>
        {onChange ? (
          <button
            type="button"
            onClick={onChange}
            className="mj-press min-h-[40px] rounded-full px-4 text-[13px] font-semibold"
            style={{ ...CONTROL, color: SOFT.ink }}
          >
            {t("ai_concierge.proposal.change")}
          </button>
        ) : null}
      </div>
      )}
    </section>
  );
}

/**
 * C11: the request went out through the place's form. Says what was sent (as
 * the form recorded it, not as the chat proposed it), what happens next, and
 * where to reach us. Never "booked": the team still has to confirm it.
 */
export function RequestSentCard({
  proposal,
  sent,
  lang,
}: {
  proposal: BookingProposal;
  sent: RequestSentInfo;
  lang: ChatLang;
}) {
  const { t } = useTranslation();
  const camp = paysByLink(proposal.slug);
  const meta = [
    proposal.title,
    dayLabel(sent.date, lang),
    sent.time,
    t("ai_concierge.proposal.guests", { count: sent.guests }),
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <section
      className="mj-row-in"
      style={{ ...panelStyle(20), padding: 14 }}
      aria-label={t("ai_concierge.proposal.sent_title")}
      data-testid="request-sent-card"
    >
      <div className="flex items-center gap-3">
        <span
          className="mj-pop-in grid h-[30px] w-[30px] shrink-0 place-items-center rounded-full"
          style={{ background: SOFT.inkGradient, boxShadow: "inset 0 1px 0 rgba(255,255,255,0.16)" }}
          aria-hidden="true"
        >
          <Check size={16} strokeWidth={3} color={SOFT.gold} />
        </span>
        <div className="min-w-0">
          <p className="text-start text-[14px] font-bold" style={{ color: SOFT.ink }}>
            {t("ai_concierge.proposal.sent_title")}
          </p>
          <p className="break-words text-start text-[12px]" style={{ color: SOFT.body }}>
            {meta}
          </p>
        </div>
      </div>
      <p
        className="mt-2.5 rounded-[12px] px-3 py-2 text-start text-[12.5px] leading-snug"
        style={{ background: "rgba(17,14,57,0.055)", color: SOFT.ink }}
      >
        {t("ai_concierge.proposal.sent_next")}
        {camp ? ` ${t("ai_concierge.proposal.sent_next_camp")}` : ""}
      </p>
      <a
        href={WHATSAPP_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="mj-press mt-2.5 inline-flex min-h-[40px] w-full items-center justify-center gap-1.5 rounded-full text-[13px] font-semibold"
        style={{ ...CONTROL, color: SOFT.ink }}
      >
        <MessageCircle size={14} aria-hidden="true" />
        {t("ai_concierge.proposal.sent_whatsapp")}
      </a>
    </section>
  );
}
